import { AppState } from '../../../app.reducers';
import { Store } from '@ngrx/store';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getLoggedIn, getIsSigningIn, getSignInFailed } from '../../../shared/auth/redux/auth.selectors';
import { AccountService } from '../../../shared/services/auth';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { CustomValidators } from '../../../shared/validators';
import { CommunicationStateComponent } from '../../../shared/components';
import { SignInRequest } from '../../../shared/models/auth';
import { LoginAction } from '../../../shared/auth/redux/auth.actions';
import { SubSink } from 'subsink';

@Component({
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  standalone: false
})
export class SignInComponent implements OnInit, OnDestroy {

  isCheckingParameters = false;
  userEmail: string;
  resetPasswordToken: string;
  operationToken: string;
  parametersAreValid: boolean;

  checkResetPwdParmsFeedbackMessages: any[] = [];
  signInFeedbackMessages: any[] = [];
  signInForm: UntypedFormGroup;
  isSubmitting = false;
  isRequestingResetPwd = false;

  @ViewChild('signInCommState', { static: true }) public signInCommunicationState: CommunicationStateComponent;
  @ViewChild('checkResetPwdCommState', { static: true }) public checkResetPwdCommunicationState: CommunicationStateComponent;

  private subs = new SubSink();

  constructor(private router: Router,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private _accountService: AccountService) {
  }

  ngOnInit() {
    this.subs.sink = this.route.params
      .subscribe(params => {
        this.userEmail = params['email'];
        this.resetPasswordToken = params['resetPasswordToken'];
        this.operationToken = params['operationToken'];

        // In case of reset password was requested, check if paramaters are valid
        if (this.userEmail
          && this.resetPasswordToken
          && this.operationToken) {
          this.onInitResetPassword();
        }
      });

    this.initForms();

    this.handleResendEmailEvent();

    this.subs.sink = this.store.select(getLoggedIn).subscribe(x => {
      if (x === true) {
        this.goToMain();
      }
    });

    this.subs.sink = this.store.select(getIsSigningIn).subscribe(x => {
      this.isSubmitting = x;
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.subs.sink = this.store.select(getSignInFailed).subscribe(x => {
      this.handleSignInError(null);
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onInitResetPassword() {
    this.isCheckingParameters = true;
    this.subs.sink = this.initResetPassword()
      .subscribe((response) => {
        this.parametersAreValid = response.data;
        if (this.parametersAreValid) {
          this.goToResetPassword();
        }

        this.isCheckingParameters = false;
      }, (error) => this.handleCheckResetPasswordParametersError(error));
  }

  initResetPassword() {
    this.checkResetPwdParmsFeedbackMessages = [];

    const payload = {
      email: this.userEmail,
      resetPasswordToken: this.resetPasswordToken,
      operationToken: this.operationToken
    };

    return this._accountService.initResetPassword(payload);
  }

  onSignIn() {
    this.isSubmitting = true;
    this.signInCommunicationState.setStartState();
    this.checkResetPwdCommunicationState.setStartState();

    this.signIn();
  }

  signIn() {
    this.signInFeedbackMessages = [];

    const request: SignInRequest = {
      // grant_type: 'password',
      email: this.signInForm.value.email,
      password: this.signInForm.value.password,
      isPersistent: true,
      rememberBrowser: true
    };

    this.store.dispatch(new LoginAction(request));
  }

  onSubmitResetPasswordRequest() {
    this.isRequestingResetPwd = true;
    this.signInCommunicationState.setStartState();
    this.checkResetPwdCommunicationState.setStartState();

    this.router.navigate(['resetpassword'], { queryParams: { email: this.signInForm.value.email } });
  }

  submitResetPasswordRequest() {
    const payload = {
      email: this.signInForm.value.email
    };

    return this._accountService.requestResetPassword(payload);
  }

  private goToMain() {
    this.router.navigate(['useraccount/user/properties'], { state: { isLogin: true } });
  }

  private goToResetPassword() {
    this.router.navigate([`resetpassword/${this.userEmail}/${this.resetPasswordToken}/${this.operationToken}`]);
  }

  private initForms() {
    this.signInForm = new UntypedFormGroup({
      email: new UntypedFormControl(null, [Validators.required, CustomValidators.emailValidator]),
      password: new UntypedFormControl(null, [Validators.required]),
    });
  }

  private handleResendEmailEvent() {
    // display the user email
    if (this.userEmail !== undefined
      && this.resetPasswordToken === undefined
      && this.operationToken === undefined) {
      this.signInForm.controls['email'].setValue(this.userEmail);

      this.onSubmitResetPasswordRequest();
    }
  }

  private handleSignInError(errors) {
    this.isSubmitting = false;
    this.signInFeedbackMessages = errors;
    this.signInCommunicationState.setFailedState(errors);
  }

  private handleCheckResetPasswordParametersError(errors) {

    const customError = {
      source: 'The link to reset your password has expired',
      details: `If you still need to reset your password, <a href='/#/signin/${encodeURIComponent(this.userEmail)}'>send an email</a> to restart the process.`
    };
    errors = [customError];

    this.isCheckingParameters = false;
    this.checkResetPwdParmsFeedbackMessages = errors;
    this.checkResetPwdCommunicationState.failedMessage = errors[0].source;
    this.checkResetPwdCommunicationState.setFailedState(errors);
  }
}
